import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";

export interface CommitmentType {
    id: number;
    type: string;
}

export interface AfterMeetingStatus {
    id: number;
    status: string;
}

export interface LeadCommitmentResponse {
    id: string;
    name: string;
    email: string;
    city: string;
    phone: string;
}

export interface CommitmentResponse {
    id: string;
    title: string;
    date: string;
    begin: string;
    end: string;
    commitmentType: CommitmentType;
    afterMeetingStatus: AfterMeetingStatus;
    priority: number;
    lead: LeadCommitmentResponse;
    link?: string;
    address?: string;
    comment?: string;
}

export interface CommitmentByWeekResponse {
    date: string;
    commitments: CommitmentResponse[];
}

export interface CreateCommitmentRequest {
    leadId: string;
    title: string;
    date: string;
    begin: string;
    end: string;
    commitmentTypeId: number;
    priority: number;
    link?: string;
    address?: string;
    comment?: string;
}

export interface UpdateCommitmentRequest extends CreateCommitmentRequest {
    id: string;
    afterMeetingStatusId: number;
}

export interface RescheduleCommitmentRequest extends CreateCommitmentRequest {
    commitmentId: string;
}

export interface CommitmentsPaginationResponse {
    items: CommitmentResponse[];
    pageNumber: number;
    pageSize: number;
    totalItems: number;
}

export default function useCommitment() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);

    const [commitments, setCommitments] = useState<CommitmentResponse[]>([]);
    const [weeklyCommitments, setWeeklyCommitments] = useState<CommitmentByWeekResponse[]>([]);
    const [historic, setHistoric] = useState<CommitmentsPaginationResponse | null>(null);
    const [nextCommitment, setNextCommitment] = useState<CommitmentResponse | null>(null);

    async function createCommitment(request: CreateCommitmentRequest) {
        setLoading(true);
        try {
            const response = await api.post("/commitment", request);
            showToast("success", "Compromisso criado com sucesso!");
            return response.data;
        } catch (error) {
            showToast("error", "Erro ao criar compromisso.");
            console.error("Erro createCommitment:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getCommitments() {
        setLoading(true);
        try {
            const response = await api.get("/commitment");
            setCommitments(response.data.data);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar compromissos.");
            console.error("Erro getCommitments:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getByDate(date: string) {
        setLoading(true);
        try {
            const response = await api.get(`/commitment/filter?date=${date}`);
            setCommitments(response.data.data);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar compromissos por data.");
            console.error("Erro getByDate:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getByWeek(begin: string, end: string) {
        setLoading(true);
        try {
            const response = await api.get(`/commitment/week?begin=${begin}&end=${end}`);
            setWeeklyCommitments(response.data.data);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar compromissos da semana.");
            console.error("Erro getByWeek:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getById(id: string) {
        setLoading(true);
        try {
            const response = await api.get(`/commitment/${id}`);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar compromisso.");
            console.error("Erro getById:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getNext() {
        setLoading(true);
        try {
            const response = await api.get(`/commitment/next`);
            setNextCommitment(response.data.data);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar próximo compromisso.");
            console.error("Erro getNext:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function getHistoric(pageNumber: number, pageSize: number, leadId: string) {
        setLoading(true);
        try {
            const response = await api.get(
                `/commitment/historic?pageNumber=${pageNumber}&pageSize=${pageSize}&leadId=${leadId}`
            );
            setHistoric(response.data.data);
            return response.data.data;
        } catch (error) {
            showToast("error", "Erro ao buscar histórico.");
            console.error("Erro getHistoric:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function updateCommitment(request: UpdateCommitmentRequest) {
        setLoading(true);
        try {
            const response = await api.put("/commitment", request);
            showToast("success", "Compromisso atualizado.");
            return response.data;
        } catch (error) {
            showToast("error", "Erro ao atualizar compromisso.");
            console.error("Erro updateCommitment:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function rescheduleCommitment(request: RescheduleCommitmentRequest) {
        setLoading(true);
        try {
            const response = await api.patch("/commitment/reschedule", request);
            showToast("success", "Compromisso remarcado.");
            return response.data;
        } catch (error) {
            showToast("error", "Erro ao remarcar compromisso.");
            console.error("Erro rescheduleCommitment:", error);
            return null;
        } finally {
            setLoading(false);
        }
    }

    async function deleteCommitment(id: string) {
        setLoading(true);
        try {
            await api.delete(`/commitment/${id}`);
            showToast("success", "Compromisso excluído.");
            setCommitments(prev => prev.filter(c => c.id !== id));
        } catch (error) {
            showToast("error", "Erro ao excluir compromisso.");
            console.error("Erro deleteCommitment:", error);
        } finally {
            setLoading(false);
        }
    }

    return {
        commitments,
        weeklyCommitments,
        historic,
        nextCommitment,
        createCommitment,
        getCommitments,
        getByDate,
        getByWeek,
        getById,
        getNext,
        getHistoric,
        updateCommitment,
        rescheduleCommitment,
        deleteCommitment,
        setCommitments,
        setWeeklyCommitments,
    };
}